import type {
  AIProviderConfig,
  AIProviderContentBlock,
  AIProviderMessage,
  AIProviderStreamParams,
  AIProviderToolDefinition,
  AIResponseMetadata,
  AIUsage,
} from "../../../types/ai";

// Opportunistic HTTP/2 multiplexing for outbound HTTPS (Bun 1.3.14+).
// The `protocol` option lands in @types/bun 1.3.14; widen locally for now.
import { instrumentAIProvider } from "./instrumentation";
import { ProviderError } from "../errors/providerError";
import { isOpenAIReasoningModel, openaiEffortValue } from "./reasoning";
// Hard-skip on non-HTTPS — Bun's h2 client throws HTTP2Unsupported on h2c.
type H2Init = RequestInit & { protocol?: "http2" };
const h2IfHttps = (url: string): H2Init =>
  url.startsWith("https://") ? { protocol: "http2" } : {};

export type OpenAIConfig = {
  apiKey?: string;
  baseUrl?: string;
  fetch?: typeof globalThis.fetch;
  headers?:
    | HeadersInit
    | ((params: AIProviderStreamParams) => HeadersInit | Promise<HeadersInit>);
  modelForCapabilities?: (model: string) => string;
  providerName?: string;
  tokenSource?: () => Promise<string> | string;
  transformRequestBody?: (
    body: Record<string, unknown>,
    params: AIProviderStreamParams,
  ) => Record<string, unknown>;
};

type OpenAIMessage = {
  content: string | Array<Record<string, unknown>> | null;
  role: "user" | "assistant" | "system" | "tool";
  tool_call_id?: string;
  tool_calls?: Array<{
    function: { arguments: string; name: string };
    id: string;
    type: "function";
  }>;
};

type PendingToolCall = {
  arguments: string;
  id: string;
  name: string;
};

type UsageRef = {
  current: AIUsage | undefined;
};

type MetadataRef = {
  current: AIResponseMetadata | undefined;
};

type StreamState = {
  buffer: string;
  metadataRef: MetadataRef;
  pendingToolCalls: Map<number, PendingToolCall>;
  providerName: string;
  usageRef: UsageRef;
};

const DEFAULT_BASE_URL = "https://api.openai.com";
const SSE_DATA_PREFIX_LENGTH = 6;
const DONE_SENTINEL = "[DONE]";
const NOT_FOUND = -1;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isRecordArray = (
  value: unknown,
): value is Array<Record<string, unknown>> =>
  Array.isArray(value) && value.length > 0 && isRecord(value[0]);

const hasArrayContent = (
  msg: AIProviderMessage,
): msg is AIProviderMessage & { content: AIProviderContentBlock[] } =>
  typeof msg.content !== "string" && Array.isArray(msg.content);

const buildToolMessages = (blocks: AIProviderContentBlock[]) => {
  const toolUseBlocks = blocks.filter((block) => block.type === "tool_use");
  const toolResultBlocks = blocks.filter(
    (block) => block.type === "tool_result",
  );
  const messages: OpenAIMessage[] = [];

  if (toolUseBlocks.length > 0) {
    messages.push({
      content: null,
      role: "assistant",
      tool_calls: toolUseBlocks.map((block) => ({
        function: {
          arguments:
            typeof block.input === "string"
              ? block.input
              : JSON.stringify(block.input),
          name: block.name,
        },
        id: block.id,
        type: "function" as const,
      })),
    });
  }

  for (const result of toolResultBlocks) {
    messages.push({
      content: typeof result.content === "string" ? result.content : "",
      role: "tool",
      tool_call_id: result.tool_use_id,
    });
  }

  return messages;
};

const processMessageAtIndex = (
  result: OpenAIMessage[],
  msg: AIProviderMessage,
  idx: number,
) => {
  if (!hasArrayContent(msg)) {
    return;
  }

  const hasToolBlocks = msg.content.some(
    (block) => block.type === "tool_use" || block.type === "tool_result",
  );

  if (!hasToolBlocks) {
    return;
  }

  const toolMessages = buildToolMessages(msg.content);
  result.splice(idx, 1, ...toolMessages);
};

const convertSingleMessage = (
  result: OpenAIMessage[],
  msg: AIProviderMessage | undefined,
  idx: number,
) => {
  if (!msg) {
    return;
  }

  processMessageAtIndex(result, msg, idx);
};

const convertToolResultMessages = (
  messages: OpenAIMessage[],
  params: AIProviderStreamParams,
) => {
  const result = [...messages];

  for (let idx = 0; idx < params.messages.length; idx++) {
    convertSingleMessage(result, params.messages[idx], idx);
  }

  return result;
};

const mapToolDefinitions = (tools: AIProviderToolDefinition[]) =>
  tools.map((tool) => ({
    function: {
      description: tool.description,
      name: tool.name,
      parameters: tool.input_schema,
    },
    type: "function",
  }));

const mapContentBlockToOpenAI = (block: AIProviderContentBlock) => {
  if (block.type === "image") {
    return {
      image_url: {
        url:
          block.source.type === "url"
            ? block.source.url
            : `data:${block.source.media_type};base64,${block.source.data}`,
      },
      type: "image_url",
    };
  }

  if (block.type === "document") {
    return {
      file: {
        file_data:
          block.source.type === "url"
            ? block.source.url
            : `data:${block.source.media_type};base64,${block.source.data}`,
        filename: block.name ?? "document.pdf",
      },
      type: "file",
    };
  }

  if (block.type === "audio") {
    return {
      input_audio: { data: block.source.data, format: block.source.format },
      type: "input_audio",
    };
  }

  if (block.type === "video") {
    return {
      type: "video_url",
      video_url: {
        url:
          block.source.type === "url"
            ? block.source.url
            : `data:${block.source.media_type};base64,${block.source.data}`,
      },
    };
  }

  if (block.type === "text") {
    return { text: block.content, type: "text" };
  }

  return null;
};

const mapOpenAIContent = (msg: AIProviderStreamParams["messages"][number]) => {
  if (typeof msg.content === "string") {
    return msg.content;
  }

  const hasMedia = msg.content.some(
    (block) =>
      block.type === "image" ||
      block.type === "document" ||
      block.type === "audio" ||
      block.type === "video",
  );

  if (!hasMedia) {
    return null;
  }

  return msg.content
    .map(mapContentBlockToOpenAI)
    .filter((mapped) => mapped !== null);
};

const buildRequestBody = (
  params: AIProviderStreamParams,
  capabilityModel = params.model,
) => {
  const messages = convertToolResultMessages(
    params.messages.map((msg) => ({
      content: mapOpenAIContent(msg),
      role: msg.role,
    })),
    params,
  );

  if (params.systemPrompt) {
    messages.unshift({ content: params.systemPrompt, role: "system" });
  }

  const body: Record<string, unknown> = {
    messages,
    model: params.model,
    stream: true,
    stream_options: { include_usage: true },
  };

  if (params.tools && params.tools.length > 0) {
    body.tools = mapToolDefinitions(params.tools);
    if (
      params.toolChoice === "auto" ||
      params.toolChoice === "none" ||
      params.toolChoice === "required"
    ) {
      body.tool_choice = params.toolChoice;
    } else if (params.toolChoice && typeof params.toolChoice === "object") {
      body.tool_choice = {
        function: { name: params.toolChoice.name },
        type: "function",
      };
    }
    if (typeof params.parallelToolCalls === "boolean") {
      body.parallel_tool_calls = params.parallelToolCalls;
    }
  }

  // Reasoning models (o-series, GPT-5) reject temperature/top_p and use
  // `max_completion_tokens`; they take a `reasoning_effort` dial instead. Other
  // models take the usual sampling params and ignore reasoning.
  if (isOpenAIReasoningModel(capabilityModel)) {
    if (typeof params.maxTokens === "number") {
      body.max_completion_tokens = params.maxTokens;
    }
    if (params.reasoning) {
      const effort = openaiEffortValue(capabilityModel, params.reasoning);
      if (effort) body.reasoning_effort = effort;
    }
  } else {
    if (typeof params.temperature === "number") {
      body.temperature = params.temperature;
    }
    if (typeof params.topP === "number") body.top_p = params.topP;
    if (typeof params.maxTokens === "number")
      body.max_tokens = params.maxTokens;
  }
  if (params.stopSequences && params.stopSequences.length > 0)
    body.stop = params.stopSequences;
  if (typeof params.seed === "number") body.seed = params.seed;
  if (typeof params.frequencyPenalty === "number")
    body.frequency_penalty = params.frequencyPenalty;
  if (typeof params.presencePenalty === "number")
    body.presence_penalty = params.presencePenalty;

  if (params.responseFormat) {
    if (
      params.responseFormat.type === "text" ||
      params.responseFormat.type === "json_object"
    ) {
      body.response_format = { type: params.responseFormat.type };
    } else if (params.responseFormat.type === "json_schema") {
      body.response_format = {
        json_schema: {
          name: params.responseFormat.name,
          schema: params.responseFormat.schema,
          strict: params.responseFormat.strict ?? true,
        },
        type: "json_schema",
      };
    }
  }

  return body;
};

const parseToolInput = (rawArguments: string) => {
  try {
    return JSON.parse(rawArguments);
  } catch {
    return rawArguments;
  }
};

const flushPendingToolCalls = function* (
  pendingToolCalls: Map<number, PendingToolCall>,
) {
  for (const [, tool] of pendingToolCalls) {
    const input = parseToolInput(tool.arguments);
    yield {
      id: tool.id,
      input,
      name: tool.name,
      type: "tool_use" as const,
    };
  }

  pendingToolCalls.clear();
};

const extractUsage = (
  parsedUsage: Partial<Record<string, number>>,
): AIUsage => {
  const prompt = parsedUsage.prompt_tokens ?? 0;
  const cached = parsedUsage.cached_tokens ?? 0;

  // OpenAI's prompt_tokens INCLUDES cached input tokens. Split the cached
  // portion out (as cacheReadInputTokens) and report only the uncached part as
  // inputTokens — mirroring Anthropic, where input_tokens excludes cache. This
  // lets a cache-aware consumer discount cached input (~10% of full price)
  // instead of billing it at the full input rate.
  return {
    cacheReadInputTokens: cached,
    cacheWriteInputTokens: parsedUsage.cache_write_tokens || undefined,
    costCredits: parsedUsage.cost,
    inputTokens: Math.max(0, prompt - cached),
    outputTokens: parsedUsage.completion_tokens ?? 0,
    reasoningTokens: parsedUsage.reasoning_tokens || undefined,
    upstreamInferenceCostCredits:
      parsedUsage.upstream_inference_cost || undefined,
  };
};

const resolveToolCallIndex = (toolCall: Record<string, unknown>) => {
  const raw = typeof toolCall.index === "number" ? toolCall.index : NOT_FOUND;

  return raw < 0 ? undefined : raw;
};

const initPendingToolCall = (
  toolCall: Record<string, unknown>,
  func: Record<string, unknown> | null,
  index: number,
  pendingToolCalls: Map<number, PendingToolCall>,
) => {
  if (pendingToolCalls.has(index)) {
    return;
  }

  const toolId = typeof toolCall.id === "string" ? toolCall.id : "";
  const toolName = func && typeof func.name === "string" ? func.name : "";

  pendingToolCalls.set(index, {
    arguments: "",
    id: toolId,
    name: toolName,
  });
};

const updatePendingToolCall = (
  toolCall: Record<string, unknown>,
  func: Record<string, unknown> | null,
  pending: PendingToolCall,
) => {
  if (typeof toolCall.id === "string") {
    pending.id = toolCall.id;
  }

  if (func && typeof func.name === "string") {
    pending.name = func.name;
  }

  if (func && typeof func.arguments === "string") {
    pending.arguments += func.arguments;
  }
};

const processToolCallDelta = (
  toolCall: Record<string, unknown>,
  pendingToolCalls: Map<number, PendingToolCall>,
) => {
  const index = resolveToolCallIndex(toolCall);
  if (index === undefined) {
    return;
  }

  const func = isRecord(toolCall.function) ? toolCall.function : null;
  initPendingToolCall(toolCall, func, index, pendingToolCalls);

  const pending = pendingToolCalls.get(index);
  if (!pending) {
    return;
  }

  updatePendingToolCall(toolCall, func, pending);
};

const processToolCallDeltas = (
  toolCalls: Array<Record<string, unknown>>,
  pendingToolCalls: Map<number, PendingToolCall>,
) => {
  for (const toolCall of toolCalls) {
    processToolCallDelta(toolCall, pendingToolCalls);
  }
};

const processDelta = function* (
  delta: Record<string, unknown>,
  pendingToolCalls: Map<number, PendingToolCall>,
) {
  if (typeof delta.content === "string") {
    yield { content: delta.content, type: "text" as const };
  }

  if (isRecord(delta.audio)) {
    const audio = delta.audio;
    if (
      typeof audio.data === "string" ||
      typeof audio.transcript === "string"
    ) {
      yield {
        audioId: typeof audio.id === "string" ? audio.id : undefined,
        data: typeof audio.data === "string" ? audio.data : "",
        format: typeof audio.format === "string" ? audio.format : "pcm16",
        transcript:
          typeof audio.transcript === "string" ? audio.transcript : undefined,
        type: "audio" as const,
      };
    }
  }

  if (isRecordArray(delta.tool_calls)) {
    processToolCallDeltas(delta.tool_calls, pendingToolCalls);
  }

  if (Array.isArray(delta.annotations)) {
    for (const annotation of delta.annotations) {
      if (!isRecord(annotation) || annotation.type !== "url_citation") continue;
      const citation = isRecord(annotation.url_citation)
        ? annotation.url_citation
        : annotation;
      if (typeof citation.url !== "string") continue;
      yield {
        content:
          typeof citation.content === "string" ? citation.content : undefined,
        endIndex:
          typeof citation.end_index === "number"
            ? citation.end_index
            : undefined,
        startIndex:
          typeof citation.start_index === "number"
            ? citation.start_index
            : undefined,
        title: typeof citation.title === "string" ? citation.title : undefined,
        type: "citation" as const,
        url: citation.url,
      };
    }
  }
};

const narrowResponseMetadata = (
  parsed: Record<string, unknown>,
): AIResponseMetadata | undefined => {
  const providerMetadata = isRecord(parsed.openrouter_metadata)
    ? parsed.openrouter_metadata
    : undefined;
  const generationId = typeof parsed.id === "string" ? parsed.id : undefined;
  const model = typeof parsed.model === "string" ? parsed.model : undefined;
  const serviceTier =
    typeof parsed.service_tier === "string" ? parsed.service_tier : undefined;
  const selected =
    providerMetadata && isRecord(providerMetadata.endpoints)
      ? providerMetadata.endpoints.available
      : undefined;
  const selectedEndpoint = Array.isArray(selected)
    ? selected.find((entry) => isRecord(entry) && entry.selected === true)
    : undefined;
  const provider =
    isRecord(selectedEndpoint) && typeof selectedEndpoint.provider === "string"
      ? selectedEndpoint.provider
      : undefined;
  if (!providerMetadata && !generationId && !model && !serviceTier) return;

  return { generationId, model, provider, providerMetadata, serviceTier };
};

const processChoice = function* (
  choice: Record<string, unknown>,
  pendingToolCalls: Map<number, PendingToolCall>,
) {
  const delta = isRecord(choice.delta) ? choice.delta : null;
  if (delta) {
    yield* processDelta(delta, pendingToolCalls);
  }

  if (choice.finish_reason === "tool_calls") {
    yield* flushPendingToolCalls(pendingToolCalls);
  }
};

const narrowUsageRecord = (parsed: Record<string, unknown>) => {
  if (!isRecord(parsed.usage)) {
    return undefined;
  }

  const { usage } = parsed;
  const promptTokens =
    typeof usage.prompt_tokens === "number" ? usage.prompt_tokens : 0;
  const completionTokens =
    typeof usage.completion_tokens === "number" ? usage.completion_tokens : 0;
  const cachedTokens =
    isRecord(usage.prompt_tokens_details) &&
    typeof usage.prompt_tokens_details.cached_tokens === "number"
      ? usage.prompt_tokens_details.cached_tokens
      : 0;
  const cacheWriteTokens =
    isRecord(usage.prompt_tokens_details) &&
    typeof usage.prompt_tokens_details.cache_write_tokens === "number"
      ? usage.prompt_tokens_details.cache_write_tokens
      : 0;
  const reasoningTokens =
    isRecord(usage.completion_tokens_details) &&
    typeof usage.completion_tokens_details.reasoning_tokens === "number"
      ? usage.completion_tokens_details.reasoning_tokens
      : 0;
  const cost = typeof usage.cost === "number" ? usage.cost : undefined;
  const upstreamInferenceCost =
    isRecord(usage.cost_details) &&
    typeof usage.cost_details.upstream_inference_cost === "number"
      ? usage.cost_details.upstream_inference_cost
      : undefined;

  const normalized = extractUsage({
    cache_write_tokens: cacheWriteTokens,
    cached_tokens: cachedTokens,
    completion_tokens: completionTokens,
    cost,
    prompt_tokens: promptTokens,
    reasoning_tokens: reasoningTokens,
    upstream_inference_cost: upstreamInferenceCost,
  });
  if (isRecord(usage.server_tool_use)) {
    normalized.serverToolUse = Object.fromEntries(
      Object.entries(usage.server_tool_use).filter(
        (entry): entry is [string, number] => typeof entry[1] === "number",
      ),
    );
  }

  return normalized;
};

const processSSELine = function* (
  line: string,
  pendingToolCalls: Map<number, PendingToolCall>,
  providerName: string,
) {
  const trimmed = line.trim();
  if (!trimmed || !trimmed.startsWith("data: ")) {
    return;
  }

  const data = trimmed.slice(SSE_DATA_PREFIX_LENGTH);
  if (data === DONE_SENTINEL) {
    yield* flushPendingToolCalls(pendingToolCalls);

    return;
  }

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(data);
  } catch {
    return;
  }

  if (isRecord(parsed.error)) {
    const error = parsed.error;
    const metadata = isRecord(error.metadata) ? error.metadata : undefined;
    const status = typeof error.code === "number" ? error.code : null;
    const type =
      metadata && typeof metadata.error_type === "string"
        ? metadata.error_type
        : typeof error.error_type === "string"
          ? error.error_type
          : null;
    throw new ProviderError({
      message:
        typeof error.message === "string"
          ? error.message
          : "OpenRouter stream failed",
      metadata,
      provider: providerName,
      retryable:
        status === 408 ||
        status === 409 ||
        status === 425 ||
        status === 429 ||
        (status !== null && status >= 500),
      status,
      type,
    });
  }

  const usageUpdate = narrowUsageRecord(parsed);
  if (usageUpdate) {
    yield { type: "usage_update" as const, usage: usageUpdate };
  }

  const metadata = narrowResponseMetadata(parsed);
  if (metadata) {
    yield { metadata, type: "response_metadata" as const };
  }

  const { choices } = parsed;
  if (!isRecordArray(choices)) {
    return;
  }

  const [firstChoice] = choices;
  if (!firstChoice) {
    return;
  }

  if (isRecord(firstChoice.error)) {
    const error = firstChoice.error;
    const metadata = isRecord(error.metadata) ? error.metadata : undefined;
    const status = typeof error.code === "number" ? error.code : null;
    throw new ProviderError({
      message:
        typeof error.message === "string"
          ? error.message
          : "OpenRouter stream failed",
      metadata,
      provider: providerName,
      retryable: status === 429 || (status !== null && status >= 500),
      status,
      type:
        metadata && typeof metadata.error_type === "string"
          ? metadata.error_type
          : null,
    });
  }

  yield* processChoice(firstChoice, pendingToolCalls);
};

const isUsageUpdate = (chunk: {
  type: string;
  usage?: AIUsage;
}): chunk is { type: "usage_update"; usage: AIUsage } =>
  chunk.type === "usage_update";

const collectYieldableChunks = (
  line: string,
  pendingToolCalls: Map<number, PendingToolCall>,
  usageRef: UsageRef,
  metadataRef: MetadataRef,
  providerName: string,
) => {
  const allChunks = Array.from(
    processSSELine(line, pendingToolCalls, providerName),
  );
  const usageChunks = allChunks.filter(isUsageUpdate);
  const lastUsage = usageChunks.at(NOT_FOUND);

  if (lastUsage) {
    usageRef.current = lastUsage.usage;
  }

  const metadataChunks = allChunks.filter(
    (chunk) => chunk.type === "response_metadata",
  );
  const lastMetadata = metadataChunks.at(NOT_FOUND);
  if (lastMetadata && "metadata" in lastMetadata) {
    metadataRef.current = {
      ...metadataRef.current,
      ...lastMetadata.metadata,
      providerMetadata: {
        ...metadataRef.current?.providerMetadata,
        ...lastMetadata.metadata.providerMetadata,
      },
    };
  }

  return allChunks.filter(
    (chunk) => !isUsageUpdate(chunk) && chunk.type !== "response_metadata",
  );
};

const processSSELines = function* (
  lines: string[],
  pendingToolCalls: Map<number, PendingToolCall>,
  usageRef: UsageRef,
  metadataRef: MetadataRef,
  providerName: string,
) {
  for (const line of lines) {
    yield* collectYieldableChunks(
      line,
      pendingToolCalls,
      usageRef,
      metadataRef,
      providerName,
    );
  }
};

const processStreamValue = (
  value: Uint8Array,
  decoder: TextDecoder,
  state: StreamState,
) => {
  state.buffer += decoder.decode(value, { stream: true });
  const lines = state.buffer.split("\n");
  state.buffer = lines.pop() ?? "";

  return lines;
};

const drainReader = async function* (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  decoder: TextDecoder,
  state: StreamState,
  signal?: AbortSignal,
) {
  /* eslint-disable no-await-in-loop */
  for (
    let result = await reader.read();
    !result.done && !signal?.aborted;
    result = await reader.read()
  ) {
    /* eslint-enable no-await-in-loop */
    const lines = processStreamValue(result.value, decoder, state);
    yield* processSSELines(
      lines,
      state.pendingToolCalls,
      state.usageRef,
      state.metadataRef,
      state.providerName,
    );
  }
};

const parseSSEStream = async function* (
  body: ReadableStream<Uint8Array>,
  initialMetadata?: AIResponseMetadata,
  providerName = "openai",
  signal?: AbortSignal,
) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const state: StreamState = {
    buffer: "",
    metadataRef: { current: initialMetadata },
    pendingToolCalls: new Map<number, PendingToolCall>(),
    providerName,
    usageRef: { current: undefined },
  };

  try {
    yield* drainReader(reader, decoder, state, signal);
    yield {
      metadata: state.metadataRef.current,
      type: "done" as const,
      usage: state.usageRef.current,
    };
  } finally {
    reader.releaseLock();
  }
};

const fetchOpenAIStream = async function* (
  baseUrl: string,
  apiKey: string,
  body: Record<string, unknown>,
  fetchImpl: typeof globalThis.fetch,
  headers: HeadersInit,
  providerName: string,
  signal?: AbortSignal,
) {
  const target = `${baseUrl}/v1/chat/completions`;
  const requestHeaders = new Headers(headers);
  requestHeaders.set("Authorization", `Bearer ${apiKey}`);
  requestHeaders.set("Content-Type", "application/json");
  const response = await fetchImpl(target, {
    ...h2IfHttps(target),
    body: JSON.stringify(body),
    headers: requestHeaders,
    method: "POST",
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw ProviderError.fromResponse(providerName, response.status, errorText);
  }

  if (!response.body) {
    throw new ProviderError({
      message: `${providerName} API returned no response body`,
      provider: providerName,
      retryable: true,
    });
  }

  yield* parseSSEStream(
    response.body,
    {
      generationId: response.headers.get("X-Generation-Id") ?? undefined,
      providerMetadata: {
        cacheAge: response.headers.get("X-OpenRouter-Cache-Age") ?? undefined,
        cacheSourceId:
          response.headers.get("X-OpenRouter-Cache-Source-Id") ?? undefined,
        cacheStatus:
          response.headers.get("X-OpenRouter-Cache-Status") ?? undefined,
        cacheTtl: response.headers.get("X-OpenRouter-Cache-TTL") ?? undefined,
      },
    },
    providerName,
    signal,
  );
};

export const openai = (config: OpenAIConfig): AIProviderConfig => {
  const baseUrl = config.baseUrl ?? DEFAULT_BASE_URL;
  const fetchImpl = config.fetch ?? globalThis.fetch;
  const providerName = config.providerName ?? "openai";
  if (!config.apiKey && !config.tokenSource) {
    throw new Error("openai() requires either apiKey or tokenSource");
  }
  const resolveKey = async () => {
    if (config.tokenSource) {
      return await Promise.resolve(config.tokenSource());
    }
    return config.apiKey!;
  };
  const resolveHeaders = async (params: AIProviderStreamParams) =>
    typeof config.headers === "function"
      ? await config.headers(params)
      : (config.headers ?? {});

  return instrumentAIProvider(
    {
      stream: (params: AIProviderStreamParams) => {
        const openaiBody = buildRequestBody(
          params,
          config.modelForCapabilities?.(params.model) ?? params.model,
        );
        const body = config.transformRequestBody
          ? config.transformRequestBody(openaiBody, params)
          : openaiBody;
        return (async function* () {
          const [apiKey, headers] = await Promise.all([
            resolveKey(),
            resolveHeaders(params),
          ]);
          yield* fetchOpenAIStream(
            baseUrl,
            apiKey,
            body,
            fetchImpl,
            headers,
            providerName,
            params.signal,
          );
        })();
      },
    },
    providerName,
  );
};
